import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { type CaraWorkspaceFieldsFragment, CaraWorkspaceTool } from '@oysterjs/ais/api/graphql';
import { SanitizedMarkdown } from '@oysterjs/ais/components/sanitized-markdown';
import { AnimateIn } from '@oysterjs/ui/transition';

import { getStepTitle } from '@oysterjs/ais/components/workspaces/lib/step-title';
import { getStepContent } from '@oysterjs/ais/components/workspaces/lib/step-content';
import { useLiveElapsed } from '@oysterjs/ais/components/workspaces/hooks/use-live-elapsed';
import { formatElapsed } from '@oysterjs/ais/components/workspaces/hooks/use-workspace-run-timer';
import { SearchIntegrationStep } from './search-integration-step';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];

const InProgressDot = () => (
  <span className="relative inline-flex size-2 shrink-0">
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-300 dark:bg-blue-500/30 opacity-75" />
    <span className="relative inline-flex size-2 rounded-full bg-blue-500" />
  </span>
);

const CompletedDot = ({ error }: { error?: boolean }) => (
  <span
    className={clsx(
      'inline-flex size-2 shrink-0 rounded-full ring-2 ring-neutral-50 dark:ring-neutral-900',
      error ? 'bg-red-300 dark:bg-red-500/30' : 'bg-neutral-300 dark:bg-neutral-600'
    )}
  />
);

/**
 * Returns the in-progress event (if any) and the count of "background" steps
 * (events that are not the active in-progress step) for compact-view metadata.
 */
const summarize = (events: StageEvent[]) => {
  const inProgress = events.find((e) => !e.endedAt);
  const backgroundCount = inProgress ? events.length - 1 : events.length;
  return { inProgress, backgroundCount };
};

const groupElapsedRange = (
  events: StageEvent[]
): { startedAt: Date | null; endedAt: Date | null } => {
  if (events.length === 0) return { startedAt: null, endedAt: null };
  const startedAt = events[0]!.startedAt;
  const allEnded = events.every((e) => !!e.endedAt);
  if (!allEnded) return { startedAt, endedAt: null };
  const endedAt = events
    .map((e) => e.endedAt!)
    .reduce((latest, t) => (new Date(t).getTime() > new Date(latest).getTime() ? t : latest));
  return { startedAt, endedAt };
};

const stepsLabel = (n: number) => (n === 1 ? '1 step' : `${n} steps`);

/** Pixel height of a row's truncated content area when its full body is hidden. */
const ROW_COLLAPSED_HEIGHT_PX = 64;

/** What a row draws below its title, or null when it has nothing to show. */
const rowBody = (event: StageEvent, content: string) => {
  // A SEARCH_INTEGRATION step draws its tool call rather than a markdown body.
  const searchIntegrationTool =
    event.toolCall?.type === CaraWorkspaceTool.SearchIntegration
      ? event.toolCall.searchIntegrationTool
      : null;
  if (searchIntegrationTool) {
    if (!searchIntegrationTool.input.find) return null;
    return {
      markdown: false,
      element: <SearchIntegrationStep tool={searchIntegrationTool} failed={!!event.error} />
    };
  }
  if (!content.trim()) return null;
  return { markdown: true, element: <SanitizedMarkdown content={content} /> };
};

/**
 * A row inside the expanded timeline. Renders the event's title with a soft
 * preview of the content below; on hover, an expand button appears that, when
 * clicked, expands the row to reveal the full body.
 *
 * Content collapse/expand animates `height` between a fixed collapsed value
 * and `'auto'`, so the natural-size body simply grows/shrinks the row and
 * pushes neighbours instead of being scaled into place.
 *
 * If the content's natural height fits within the collapsed height the row is
 * never truncated: the toggle is disabled and the fade gradient is hidden.
 * The measurement uses a ResizeObserver on the inner (unclipped) content div
 * so it stays accurate if the content changes after mount.
 */
const StageEventTimelineRow = (props: {
  event: StageEvent;
  expanded: boolean;
  onToggleExpanded: () => void;
}) => {
  const { event, expanded, onToggleExpanded } = props;

  const completed = !!event.endedAt;
  const error = !!event.error;
  const title = getStepTitle(event) ?? 'Step';
  const content = getStepContent(event);
  const body = rowBody(event, content);
  const hasContent = !!body;

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [fitsInCollapsed, setFitsInCollapsed] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const check = () => setFitsInCollapsed(el.offsetHeight <= ROW_COLLAPSED_HEIGHT_PX);
    check();
    const obs = new ResizeObserver(check);
    obs.observe(el);
    return () => obs.disconnect();
  }, [content, hasContent]);

  const canToggle = hasContent && !fitsInCollapsed;
  const indicator = !completed ? <InProgressDot /> : <CompletedDot error={error} />;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="group/row relative pl-6 pb-3 last:pb-0"
    >
      <div className="absolute left-0 top-[-4px]">{indicator}</div>

      <Headless.Button
        className="flex w-full flex-row items-start justify-between gap-2 text-left"
        onClick={canToggle ? onToggleExpanded : undefined}
        disabled={!canToggle}
      >
        <span
          className={clsx(
            'text-sm font-medium',
            completed
              ? 'text-neutral-700 dark:text-neutral-300'
              : 'text-neutral-900 dark:text-white'
          )}
        >
          {title}
        </span>
      </Headless.Button>

      {body && (
        <motion.div
          initial={false}
          animate={{ height: fitsInCollapsed || expanded ? 'auto' : ROW_COLLAPSED_HEIGHT_PX }}
          transition={{ type: 'tween', duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="relative mt-1 overflow-hidden"
        >
          <div
            ref={contentRef}
            className={clsx(
              'text-xs leading-relaxed text-neutral-500 dark:text-neutral-400',
              body.markdown && 'unstyled-markdown-container'
            )}
          >
            {body.element}
          </div>
          <AnimatePresence initial={false}>
            {!expanded && !fitsInCollapsed ? (
              <motion.div
                key="fade"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-neutral-50 dark:from-neutral-900 via-neutral-50/85 dark:via-neutral-900/85 to-transparent"
              />
            ) : (
              <div className="h-4 bg-transparent" />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </motion.div>
  );
};

/**
 * Compact-view contents shown when the group is collapsed. Displays the latest
 * in-progress step's title (or a "Thinking..." / "Completed N steps" summary),
 * plus subtle meta showing how many steps preceded it and how long the
 * current/total run has taken.
 *
 * All three states share a single `AnimatePresence`, keyed by the title's
 * identity (event id, `__thinking__`, or `__completed__`). This is critical
 * for the inter-event slide animation to play correctly even when a brief
 * "Thinking..." flash appears between events — without it, the AnimatePresence
 * would unmount on the swap and the next event title would snap into place
 * without animation.
 */
const StageEventCompactBody = (props: {
  events: StageEvent[];
  liveElapsedMs: number;
  pendingFallback: boolean;
}) => {
  const { events, liveElapsedMs, pendingFallback } = props;
  const { inProgress, backgroundCount } = summarize(events);
  const elapsedText = formatElapsed(liveElapsedMs);

  let titleKey: string;
  let titleText: string;
  let isActive: boolean;

  if (inProgress) {
    titleKey = inProgress.id;
    titleText = getStepTitle(inProgress) ?? 'Working...';
    isActive = true;
  } else if (pendingFallback) {
    titleKey = '__thinking__';
    titleText = 'Thinking...';
    isActive = true;
  } else {
    titleKey = '__completed__';
    const suffix = liveElapsedMs > 0 ? ` in ${elapsedText}` : '';
    titleText = `Completed ${stepsLabel(events.length)}${suffix}`;
    isActive = false;
  }

  return (
    <>
      <div className="flex min-w-0 items-center gap-2">
        {isActive ? <InProgressDot /> : <CompletedDot />}
        <div className="relative min-w-0 flex-1 overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={titleKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              className={clsx(
                'block truncate text-left text-sm',
                isActive
                  ? 'font-medium text-neutral-700 dark:text-neutral-300'
                  : 'text-neutral-500 dark:text-neutral-400'
              )}
            >
              {titleText}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
      {isActive && (
        <div className="flex shrink-0 items-center gap-2 text-sm text-neutral-400 dark:text-neutral-500">
          {backgroundCount > 0 && <span>{stepsLabel(backgroundCount)} done</span>}
          {backgroundCount > 0 && liveElapsedMs > 0 && <span aria-hidden>·</span>}
          {liveElapsedMs > 0 && <span className="tabular-nums">{elapsedText}</span>}
        </div>
      )}
    </>
  );
};

/**
 * Renders a contiguous run of "row-style" stage events (thoughts, searches,
 * integration writes, etc.) as a single coalesced unit. Compact by default;
 * expands on click into a vertical timeline where each event can be further
 * expanded to reveal its full content.
 *
 * Artifact-producing events are not handled here — they break out of the group
 * and render as standalone cards via the parent conversation component.
 */
export const StageEventGroup = (props: {
  events: StageEvent[];
  /**
   * When true, the workspace is currently working but no event in this group is
   * actively in progress (e.g. a brief gap between events). The compact view
   * surfaces a "Thinking..." indicator instead of the "Completed N steps"
   * summary, and the expanded view appends a placeholder in-progress row.
   */
  pendingFallback?: boolean;
}) => {
  const { events, pendingFallback = false } = props;

  const [expanded, setExpanded] = React.useState(false);
  const [expandedEventIds, setExpandedEventIds] = React.useState<Set<string>>(() => new Set());

  const { startedAt, endedAt } = React.useMemo(() => groupElapsedRange(events), [events]);
  const liveElapsedMs = useLiveElapsed(startedAt, endedAt);

  const { inProgress } = summarize(events);
  const showPending = pendingFallback && !inProgress;

  // A group holding nothing but the pending row has no timeline to reveal — the
  // expanded view would restate the row the user already sees — so it renders as
  // a plain status line with no disclosure control.
  const expandable = events.length > 0;

  const toggleEvent = React.useCallback((id: string) => {
    setExpandedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (events.length === 0 && !pendingFallback) return null;

  return (
    <AnimateIn data-conversation-role="stage-event">
      <div className="relative w-full">
        <Headless.Button
          onClick={() => setExpanded((v) => !v)}
          disabled={!expandable}
          className={clsx(
            'group/group flex flex-row gap-1.5 items-center',
            'rounded-md py-1.5',
            expandable && 'transition-colors duration-150 hover:bg-neutral-50 dark:hover:bg-white/5'
          )}
        >
          <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
            <StageEventCompactBody
              events={events}
              liveElapsedMs={liveElapsedMs}
              pendingFallback={showPending}
            />
          </div>
          {expandable && (
            <ChevronDownIcon
              className={clsx(
                'size-4 shrink-0 text-neutral-300 dark:text-neutral-600 transition-all duration-200',
                'group-hover/group:text-neutral-500 dark:group-hover/group:text-neutral-400',
                !expanded && '-rotate-90'
              )}
            />
          )}
        </Headless.Button>

        {expandable && (
          <motion.div
            initial={false}
            animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
            transition={{
              height: { type: 'tween', duration: 0.3, ease: [0.4, 0, 0.2, 1] },
              opacity: { type: 'tween', duration: 0.2, ease: 'easeOut' }
            }}
            className="overflow-hidden"
          >
            <div className="relative pt-3">
              <div
                aria-hidden
                className="absolute bottom-0 left-[3.5px] top-0 w-px bg-neutral-200 dark:bg-neutral-700"
              />
              <AnimatePresence initial={false}>
                {events.map((event) => (
                  <StageEventTimelineRow
                    key={event.id}
                    event={event}
                    expanded={expandedEventIds.has(event.id)}
                    onToggleExpanded={() => toggleEvent(event.id)}
                  />
                ))}
                {showPending && (
                  <motion.div
                    key="__pending__"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="relative pl-6 pb-3 last:pb-0"
                  >
                    <div className="absolute left-[3px] top-[7px]">
                      <InProgressDot />
                    </div>
                    <span className="text-sm font-medium text-neutral-900 dark:text-white">
                      Thinking...
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </div>
    </AnimateIn>
  );
};
